import React, { useState } from 'react';
import { toast } from 'sonner';

import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { usePatchMatterMutation } from '@/services/api/mattersService';

import { InfoBadge } from './InfoBadge';
import { InlineEditControls } from './InlineEditControls';

// LocationEditBadge has two fields (city, state) so it doesn't fit the single-field useInlineEdit hook perfectly.
// Keeping local state management but using shared UI components.

interface LocationEditBadgeProps {
  matterId: string;
  city: string | undefined | null;
  state: string | undefined | null;
  className?: string;
}

export const LocationEditBadge: React.FC<LocationEditBadgeProps> = ({
  matterId,
  city,
  state,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editCity, setEditCity] = useState(city || '');
  const [editState, setEditState] = useState(state || '');
  const [patchMatter, { isLoading }] = usePatchMatterMutation();

  const handleSave = async () => {
    const cityVal = editCity.trim();
    const stateVal = editState.trim();

    if (cityVal === (city || '') && stateVal === (state || '')) {
      setIsEditing(false);
      return;
    }

    try {
      await patchMatter({
        matter_id: matterId,
        patch: {
          city: cityVal || undefined,
          state: stateVal || undefined,
        },
      }).unwrap();
      toast.success('Location updated');
      setIsEditing(false);
    } catch (error) {
      toast.error('Failed to update location');
    }
  };

  const handleCancel = () => {
    setEditCity(city || '');
    setEditState(state || '');
    setIsEditing(false);
  };

  return (
    <Popover
      open={isEditing}
      onOpenChange={(open) => {
        if (!open) handleCancel();
        else setIsEditing(true);
      }}
    >
      <PopoverTrigger asChild>
        <InfoBadge
          label="Location"
          value={
            city || state
              ? `${city || ''}${city && state ? ', ' : ''}${state || ''}`
              : 'Set Location'
          }
          onClick={() => {
            setEditCity(city || '');
            setEditState(state || '');
            setIsEditing(true);
          }}
          showLabel={false}
          className={className}
        />
      </PopoverTrigger>
      <PopoverContent className="p-2 w-auto" align="start" side="bottom">
        <InlineEditControls
          isEditing={isEditing}
          onSave={handleSave}
          onCancel={handleCancel}
          isLoading={isLoading}
        >
          <Input
            value={editCity}
            onChange={(e) => setEditCity(e.target.value)}
            placeholder="City"
            className="h-6 text-xs w-24 px-1 py-0"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
          />
          <span className="text-zinc-400">,</span>
          <Input
            value={editState}
            onChange={(e) => setEditState(e.target.value)}
            placeholder="State"
            className="h-6 text-xs w-12 px-1 py-0"
            maxLength={2}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
          />
        </InlineEditControls>
      </PopoverContent>
    </Popover>
  );
};
